/**
 * Markers panel — list / edit / draw 3D annotation markers.
 *
 * Sections (top → bottom):
 *   - Draw button (toggles MarkersTool active state)
 *   - Visibility toggle (hides the line-mesh in the viewport)
 *   - Marker list (one row per marker, click → select)
 *   - Editor for the selected marker (title + body textareas + delete)
 *
 * Live-syncs with the Markers element via the `markers.*` event namespace.
 * Layout / class names mirror walk-path-panel so the same CSS conventions
 * apply (panel-header, *-row, *-row-label, *-buttons, *-section, *-hint).
 */

import {
    Container,
    Label,
    Button,
    BooleanInput,
    TextInput,
    Element as PcuiElement
} from '@playcanvas/pcui';

import type { Events } from '../../events';
import type { Scene } from '../../scene';
import { i18n } from '../../ui/localization';

interface MarkerData {
    id: string;
    position: { x: number; y: number; z: number };
    title: string;
    body: string;
}

class MarkersPanel extends Container {
    constructor(events: Events, scene: Scene, args = {}) {
        args = {
            ...args,
            id: 'markers-panel',
            class: 'panel',
            hidden: true
        };
        super(args);

        // Block viewport gestures from passing through.
        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick'].forEach((evt) => {
            this.dom.addEventListener(evt, (e: Event) => e.stopPropagation());
        });

        const markersEl = () => (scene as any).markers;

        // ──────────────────────────────────────────────────────────────
        // Header
        // ──────────────────────────────────────────────────────────────
        const header = new Container({ class: 'panel-header' });
        header.append(new Label({ class: 'panel-header-icon', text: '' }));
        header.append(new Label({ class: 'panel-header-label', text: i18n.t('ollo.markers.title') }));
        this.append(header);

        // ──────────────────────────────────────────────────────────────
        // Primary action: Draw mode + Visibility
        // ──────────────────────────────────────────────────────────────
        const primaryButtons = new Container({ class: 'markers-buttons' });
        const drawButton = new Button({ class: ['markers-button-primary'], text: i18n.t('ollo.markers.drop') });
        drawButton.on('click', () => events.fire('tool.markers'));
        primaryButtons.append(drawButton);
        this.append(primaryButtons);

        const helpLabel = new Label({
            class: 'markers-hint',
            text: i18n.t('ollo.markers.hint')
        });
        this.append(helpLabel);

        const visRow = new Container({ class: 'markers-row' });
        visRow.append(new Label({ class: 'markers-row-label', text: i18n.t('ollo.markers.show') }));
        const visToggle = new BooleanInput({ value: true });
        visToggle.on('change', (v: boolean) => events.fire('markers.setVisible', v));
        visRow.append(visToggle);
        this.append(visRow);

        const countRow = new Container({ class: 'markers-row' });
        countRow.append(new Label({ class: 'markers-row-label', text: i18n.t('ollo.markers.count') }));
        const countLabel = new Label({ class: 'markers-row-value', text: '0' });
        countRow.append(countLabel);
        this.append(countRow);

        // ──────────────────────────────────────────────────────────────
        // List of markers
        // ──────────────────────────────────────────────────────────────
        const listHeader = new Label({ class: 'markers-section', text: i18n.t('ollo.markers.list') });
        this.append(listHeader);

        const listContainer = new Container({ class: 'markers-list' });
        this.append(listContainer);

        const emptyHint = new Label({
            class: 'markers-hint',
            text: i18n.t('ollo.markers.empty')
        });
        this.append(emptyHint);

        // ──────────────────────────────────────────────────────────────
        // Selected-marker editor
        // ──────────────────────────────────────────────────────────────
        const editorHeader = new Label({ class: 'markers-section', text: i18n.t('ollo.markers.selected') });
        this.append(editorHeader);

        const editorContainer = new Container({ class: 'markers-editor' });
        this.append(editorContainer);

        const titleRow = new Container({ class: 'markers-row' });
        titleRow.append(new Label({ class: 'markers-row-label', text: i18n.t('ollo.markers.title-field') }));
        const titleInput = new TextInput({ value: '' });
        // TextInput in PCUI fires 'change' on commit (blur/Enter). Wire
        // both to keep the model fresh while typing.
        titleInput.on('change', (v: string) => {
            const id = markersEl()?.selectedId;
            if (id) events.fire('markers.setTitle', id, v);
        });
        titleRow.append(titleInput);
        editorContainer.append(titleRow);

        // PCUI's `class` arg takes a single token or an ARRAY of tokens.
        // A space-separated string like 'markers-row markers-row-body' is
        // passed straight to DOMTokenList.add(), which rejects tokens with
        // spaces → InvalidCharacterError → MarkersPanel constructor throws
        // → main() halts BEFORE window.scene = scene → editor goes black
        // with right-toolbar intact (the symptom we just spent an hour
        // chasing through the SW cache layer). Use the array form here and
        // anywhere else in this file that needs multiple classes.
        const bodyRow = new Container({ class: ['markers-row', 'markers-row-body'] });
        bodyRow.append(new Label({ class: 'markers-row-label', text: i18n.t('ollo.markers.body') }));
        // PCUI doesn't ship a multi-line input; we drop in a raw <textarea>
        // wrapped in a PcuiElement so it participates in the panel layout.
        const bodyTextarea = document.createElement('textarea');
        bodyTextarea.className = 'markers-body-textarea';
        bodyTextarea.rows = 3;
        bodyTextarea.placeholder = 'Description shown in the marker popover…';
        bodyTextarea.addEventListener('input', () => {
            const id = markersEl()?.selectedId;
            if (id) events.fire('markers.setBody', id, bodyTextarea.value);
        });
        const bodyWrap = new PcuiElement({ dom: bodyTextarea });
        bodyRow.append(bodyWrap);
        editorContainer.append(bodyRow);

        const positionRow = new Container({ class: 'markers-row' });
        positionRow.append(new Label({ class: 'markers-row-label', text: i18n.t('ollo.markers.position') }));
        const positionLabel = new Label({ class: 'markers-row-value', text: '—' });
        positionRow.append(positionLabel);
        editorContainer.append(positionRow);

        const editorButtons = new Container({ class: 'markers-buttons' });
        const deleteButton = new Button({ text: i18n.t('ollo.markers.delete') });
        deleteButton.on('click', () => {
            const id = markersEl()?.selectedId;
            if (id) events.fire('markers.remove', id);
        });
        editorButtons.append(deleteButton);

        const clearAllButton = new Button({ text: i18n.t('ollo.markers.clear-all') });
        clearAllButton.on('click', async () => {
            const m = markersEl();
            if (!m?.markers?.length) return;
            const r = await events.invoke('showPopup', {
                type: 'yesno',
                header: 'Clear all markers?',
                message: `Delete all ${m.markers.length} markers?`
            });
            if (r?.action === 'yes') events.fire('markers.clear');
        });
        editorButtons.append(clearAllButton);
        editorContainer.append(editorButtons);

        // ──────────────────────────────────────────────────────────────
        // Live sync — list rebuild + editor sync
        // ──────────────────────────────────────────────────────────────

        const fmt = (n: number) => n.toFixed(2);

        const rebuildList = () => {
            const m = markersEl();
            const list: MarkerData[] = m?.markers ?? [];
            countLabel.text = list.length.toString();
            emptyHint.hidden = list.length > 0;

            // Wipe rows.
            listContainer.clear();

            const selectedId = m?.selectedId ?? null;
            list.forEach((marker, i) => {
                const row = new Container({ class: 'markers-list-row' });
                if (marker.id === selectedId) row.class.add('markers-list-row-selected');

                const nameLabel = new Label({
                    class: 'markers-list-row-name',
                    text: marker.title || `(marker ${i + 1})`
                });
                row.append(nameLabel);

                const posLabel = new Label({
                    class: 'markers-list-row-pos',
                    text: `${fmt(marker.position.x)}, ${fmt(marker.position.y)}, ${fmt(marker.position.z)}`
                });
                row.append(posLabel);

                row.dom.addEventListener('click', () => {
                    events.fire('markers.select', marker.id);
                });

                listContainer.append(row);
            });
        };

        const syncEditor = () => {
            const m = markersEl();
            const list: MarkerData[] = m?.markers ?? [];
            const selectedId: string | null = m?.selectedId ?? null;
            const sel = selectedId ? list.find(x => x.id === selectedId) : null;

            editorContainer.hidden = !sel;
            if (!sel) return;

            // Only set inputs when value differs — prevents wrecking the
            // caret while the user is still typing into the same field.
            if (titleInput.value !== sel.title) titleInput.value = sel.title;
            if (bodyTextarea.value !== sel.body) bodyTextarea.value = sel.body;
            positionLabel.text = `${fmt(sel.position.x)}, ${fmt(sel.position.y)}, ${fmt(sel.position.z)}`;
        };

        const syncAll = () => {
            rebuildList();
            syncEditor();
        };

        events.on('markers.changed', syncAll);
        events.on('markers.selectionChanged', syncAll);

        events.on('markers.visibleChanged', (v: boolean) => {
            if (visToggle.value !== v) visToggle.value = v;
        });

        // Reflect tool-active state on Draw button.
        events.on('tool.activated', (toolName: string) => {
            if (toolName === 'markers') {
                drawButton.text = i18n.t('ollo.markers.dropping');
                drawButton.class.add('markers-button-active');
            }
        });
        events.on('tool.deactivated', (toolName: string) => {
            if (toolName === 'markers') {
                drawButton.text = i18n.t('ollo.markers.drop');
                drawButton.class.remove('markers-button-active');
            }
        });

        // Initial sync (markers element may not exist on construction frame).
        setTimeout(syncAll, 0);
    }
}

export { MarkersPanel };
